import { CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { SimpleDocCommand } from "./types";
import { getDocContext } from "../../services/doc/docApi";
import { DocumentUtils } from "../../services/doc/internal/yDocHighLevelAPI";
import { createYElement } from "../../services/doc/internal/yDocLowLevelAPI";
import { API_BASE_URL } from "../../../../api/config";
import * as Y from "../../../../shared/yjs";
import useUsageLimitsStore from "../../../../stores/usageLimitsStore";

type InsertLocation = { before?: string; after?: string } | { at: 'end' | 'start' | 'cursor' } | { atIndex: number };

/**
 * doc_generate_image_v2 - Flexible AI-powered image generation
 * 
 * Generates images using DALL-E 3 (default) or Grok (uncensored)
 * and inserts them into the document at the cursor or specified location.
 * Supports prompt-based refinement by combining prompts.
 */
export const docGenerateImageV2Command: SimpleDocCommand = {
  id: 'doc_generate_image_v2',
  description: 'Generate an AI image using DALL-E 3 (default, high quality) or Grok (uncensored) and insert it into a document. For refinements, combine the original prompt with modifications in the prompt parameter. IMPORTANT: A document must be open or in context.',
  logicalName: 'doc_generate_image_v2',
  version: 'v2',
  appType: 'docs',
  
  // ✅ Structured view data for UI rendering
  formatViewData: (result: any, toolInput: any) => {
    try {
      return {
        type: 'image_generation',
        summary: result?.message || 'Generated and inserted image',
        data: {
          prompt: result?.data?.prompt || toolInput?.prompt,
          imageData: result?.data?.imageData, // ← Base64 image for thumbnail
          model: result?.data?.model || result?.data?.modelName || toolInput?.model || 'DALL-E 3',
          provider: result?.data?.provider,
          size: result?.data?.size || '1792x1024',
          displayWidth: result?.data?.displayWidth || '67%',
          displayHeight: result?.data?.displayHeight || 'auto',
          alignment: result?.data?.alignment || 'center',
          imageIndex: result?.data?.imageIndex,
          operation: result?.data?.operation // 'generate_image_v2'
        },
        meta: {
          duration: result?.data?.executionTime || result?.metadata?.executionTime
        }
      };
    } catch (error) {
      console.warn('[DOC_GENERATE_IMAGE_V2] Error in formatViewData:', error);
      return {
        type: 'tool_summary',
        summary: 'Generated image',
        data: {}
      };
    }
  },
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'The document ID (llmId) to insert the image into. The document must be accessible - either currently open or previously read via doc_read_v2.',
        required: true
      },
      {
        name: 'prompt',
        type: 'string',
        description: 'Detailed description of the image to generate. Be specific and descriptive for best results.',
        required: true
      },
      {
        name: 'insertLocation',
        type: 'object',
        description: 'Where to insert the image. Options: {before: "node_id"}, {after: "node_id"}, {at: "start"}, {at: "end"}, {at: "cursor"}, or {atIndex: number}',
        required: true
      },
      {
        name: 'model',
        type: 'string',
        description: 'Model to use: "dall-e-3" (high quality, default) or "grok" (uncensored). Default: "dall-e-3"',
        required: false
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      const { id: docId, prompt, insertLocation, model = 'dall-e-3' } = params;
      
      if (!docId || !prompt || !insertLocation) {
        return createErrorResult('Missing required parameters', 'id, prompt, and insertLocation are required', 'validation');
      }
      
      console.log('[doc_generate_image_v2] Starting image generation:', {
        docId,
        prompt: prompt.substring(0, 100) + (prompt.length > 100 ? '...' : ''),
        insertLocation,
        model
      });
      
      // Get document context
      const { doc, error } = await getDocContext(docId, context);
      if (error || !doc) {
        let availableDocsHelp = '';
        try {
          const windowContents = (context as any)?.spaceContext?.entityRefs || {};
          const availableDocs = Object.keys(windowContents)
            .filter(key => key.startsWith('doc_'))
            .map(key => {
              const doc = windowContents[key];
              return `  - ${key} (${doc?.title || doc?.entityName || 'Untitled'})`;
            });
          
          if (availableDocs.length > 0) {
            availableDocsHelp = `\n\nAvailable documents in context:\n${availableDocs.join('\n')}\n\nPlease use one of these document IDs, or ask the user to open the document first.`;
          } else {
            availableDocsHelp = '\n\nNo documents are currently open or in context. Please ask the user to open a document first, or use doc_read_v2 to read an existing document into context.';
          }
        } catch (e) {
          availableDocsHelp = '\n\nCould not retrieve available documents. Please ensure a document is open before generating images.';
        }
        
        return createErrorResult(
          'Document not accessible', 
          `Cannot insert image: Document "${docId}" is not accessible. ${error || 'Document not found.'}${availableDocsHelp}`, 
          'validation'
        );
      }
      
      const { yDoc: ydoc, yxml } = doc;
      
      // STEP 1: Map model to API parameters
      const modelConfig = getModelConfig(model);
      console.log(`[doc_generate_image_v2] Using model: ${modelConfig.name}`);
      
      // STEP 2: Generate image using API
      const authToken = localStorage.getItem('authToken');
      const requestBody: any = {
        prompt,
        size: '1792x1024',
        quality: 'medium',
        uncensored: modelConfig.uncensored,
        variants: 1
      };
      
      console.log(`[doc_generate_image_v2] Calling image generation API with model: ${modelConfig.name} (uncensored: ${modelConfig.uncensored})`);
      
      const response = await fetch(`${API_BASE_URL}/image/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[doc_generate_image_v2] API error:', response.status, errorText);
        
        let errorMessage = `API error (${response.status})`;
        let userFriendlyTitle = 'Image generation failed';
        
        // Parse error details if available
        try {
          const errorData = JSON.parse(errorText);
          const detail = errorData.detail || '';

          // Plan limit exceeded — show upgrade modal instead of an in-doc error
          if (response.status === 403 && (errorData.upgrade_required || (typeof detail === 'object' && detail.upgrade_required))) {
            const limitDetail = typeof detail === 'object' ? detail : errorData;
            const limitType = (limitDetail.limit_type || 'monthly_cost') as 'monthly_cost' | 'monthlySpend' | 'teamUsage';
            const planName: string = limitDetail.plan_type || 'free';
            useUsageLimitsStore.getState().showUpgradeModal({
              limitType,
              currentUsage: {
                used: limitDetail.limits?.[limitType]?.used ?? 0,
                max: limitDetail.limits?.[limitType]?.max ?? 0,
                unit: 'images',
              },
              planName,
              message: limitDetail.message,
            });
            return createErrorResult('Image generation limit reached', 'Upgrade your plan to generate more images.', 'execution');
          }

          // Content moderation rejection
          if (detail.includes('content moderation') || detail.includes('content policy')) {
            userFriendlyTitle = 'Content blocked';
            if (model === 'grok') {
              errorMessage = 'Even Grok rejected this content. Try rephrasing your prompt to be less explicit, or try a different creative approach.';
            } else {
              errorMessage = 'OpenAI blocked this content. Try selecting Grok for more permissive content policies, or rephrase your prompt.';
            }
          }
          // Server errors
          else if (detail.includes('server had an error') || detail.includes('server_error')) {
            userFriendlyTitle = 'Service temporarily unavailable';
            errorMessage = `${modelConfig.name} is experiencing issues. Try selecting ${model === 'grok' ? 'DALL-E 3' : 'Grok'} or wait a moment and try again.`;
          }
          // Rate limiting
          else if (detail.includes('rate limit') || detail.includes('too many requests')) {
            userFriendlyTitle = 'Rate limit reached';
            errorMessage = `${modelConfig.name} rate limit reached. Please wait a moment before trying again.`;
          }
          // Generic API error with detail
          else if (detail) {
            errorMessage = detail;
          }
        } catch (e) {
          errorMessage = `${errorMessage}: ${errorText}`;
        }
        
        return createErrorResult(userFriendlyTitle, errorMessage, 'execution');
      }
      
      const result = await response.json();
      
      if (!result.success || !result.images || result.images.length === 0) {
        console.error('[doc_generate_image_v2] No images returned:', result);
        return createErrorResult('Image generation failed', 'No images returned from API', 'execution');
      }
      
      const base64Image = result.images[0];
      console.log('[doc_generate_image_v2] Image generated successfully:', {
        imageSize: base64Image.length,
        provider: result.provider,
        model: result.model,
        size: result.size
      });
      
      // STEP 3: Calculate insertion index and check if replacement
      let insertionIndex: number;
      let isReplacement = false;
      
      if ('atIndex' in insertLocation) {
        insertionIndex = insertLocation.atIndex;
        isReplacement = insertLocation.atIndex < yxml.length;
      } else if ('at' in insertLocation) {
        if (insertLocation.at === 'cursor') {
          insertionIndex = yxml.length;
        } else {
          insertionIndex = insertLocation.at === 'start' ? 0 : yxml.length;
        }
      } else if ('before' in insertLocation || 'after' in insertLocation) {
        const targetNodeId = insertLocation.before || insertLocation.after;
        const targetNode = DocumentUtils.findNodeById(yxml, targetNodeId!);
        
        if (!targetNode) {
          return createErrorResult('Target node not found', `Node ID "${targetNodeId}" does not exist in the document.`, 'validation');
        }
        
        insertionIndex = targetNode.index + (insertLocation.after ? 1 : 0);
        // If using "before", we're replacing this node
        isReplacement = !!insertLocation.before;
      } else {
        return createErrorResult('Invalid insertLocation format', 'Use {before: "node_id"}, {after: "node_id"}, {at: "start"}, {at: "end"}, {at: "cursor"}, or {atIndex: number}', 'validation');
      }
      
      insertionIndex = Math.max(0, Math.min(insertionIndex, yxml.length));
      
      console.log('[doc_generate_image_v2] Insertion index calculated:', {
        insertionIndex,
        documentLength: yxml.length,
        isReplacement
      });
      
      // STEP 4: Create Y.js image element
      const imageElement = createYElement('image', {
        attributes: {
          src: base64Image,
          alt: '',
          title: prompt.substring(0, 100),
          width: '67%',
          height: 'auto',
          alignment: 'center',
          borderRadius: '0',
          shadow: 'none'
        },
        docId: docId,
        logPrefix: '[doc_generate_image_v2]'
      });
      
      // STEP 5: Insert/Replace image in document
      if (isReplacement) {
        console.log('[doc_generate_image_v2] Replacing image at index', insertionIndex);
        
        // Instead of delete+insert, just update the existing image's src attribute
        // This avoids selection restoration issues
        ydoc.transact(() => {
          const existingNode = yxml.get(insertionIndex);
          
          if (existingNode && existingNode instanceof Y.XmlElement) {
            const nodeType = existingNode.getAttribute('type') || (existingNode as any).nodeName;
            
            if (nodeType === 'image') {
              // Update the src attribute to the new image
              existingNode.setAttribute('src', base64Image);
              existingNode.setAttribute('title', prompt.substring(0, 100));
              console.log('[doc_generate_image_v2] Updated existing image src attribute');
            } else {
              console.warn('[doc_generate_image_v2] Target not an image element (type:', nodeType, '), doing delete+insert instead');
              yxml.delete(insertionIndex, 1);
              yxml.insert(insertionIndex, [imageElement]);
            }
          } else {
            console.warn('[doc_generate_image_v2] Target not an XmlElement, doing delete+insert instead');
            yxml.delete(insertionIndex, 1);
            yxml.insert(insertionIndex, [imageElement]);
          }
        }, { origin: 'ai_image_replacement_v2' });
        
        console.log('[doc_generate_image_v2] Image replaced successfully');
      } else {
        console.log('[doc_generate_image_v2] Inserting image into document at index', insertionIndex);
        
        ydoc.transact(() => {
          yxml.insert(insertionIndex, [imageElement]);
        }, { origin: 'ai_image_generation_v2' });
        
        console.log('[doc_generate_image_v2] Image inserted successfully');
      }
      
      // Export to markdown if needed
      try {
        const { exportDocumentToMarkdown, shouldExportToMarkdown } = await import('../../utils/markdownExport');
        
        if (shouldExportToMarkdown(context)) {
          console.log('[doc_generate_image_v2] Scheduling markdown export');
          
          setTimeout(() => {
            exportDocumentToMarkdown(docId, ydoc, {
              source: 'doc_generate_image_v2',
              blocking: false,
              verbose: true
            }).catch(err => {
              console.error('[doc_generate_image_v2] Markdown export failed:', err);
            });
          }, 200);
        }
      } catch (exportError) {
        console.error('[doc_generate_image_v2] Markdown export scheduling failed (non-critical):', exportError);
      }
      
      const executionTime = Date.now() - startTime;
      
      return createSuccessResult(
        `Generated and inserted image using ${modelConfig.name}: "${prompt.substring(0, 50)}${prompt.length > 50 ? '...' : ''}"`,
        {
          operation: 'generate_image_v2',
          imageIndex: insertionIndex,
          prompt: prompt,
          imageData: base64Image, // ← Store full base64 image for thumbnail display
          model: model,
          modelName: modelConfig.name,
          size: '1792x1024',
          displayWidth: '67%',
          displayHeight: 'auto',
          alignment: 'center',
          provider: result.provider,
          executionTime
        },
        {
          format: 'json',
          metadata: {
            executionTime,
            method: 'flexible_image_generation'
          }
        }
      );
      
    } catch (error) {
      console.error('[doc_generate_image_v2] Error:', error);
      return createErrorResult(
        'Failed to generate and insert image',
        error instanceof Error ? error.message : String(error),
        'execution'
      );
    }
  }
};

function getModelConfig(model: string): { name: string; uncensored: boolean } {
  switch (model) {
    case 'dall-e-3':
      return { name: 'DALL·E 3', uncensored: false };
    case 'grok':
      return { name: 'Grok', uncensored: true };
    default:
      return { name: 'DALL·E 3', uncensored: false };
  }
}

